import React, { useState, useEffect } from "react";
import { ref, onValue, update, get } from "firebase/database";
import { db } from "./firebase";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logoPath from "./assets/x_math_logo.png";
import { formatCourseId } from "./utils/formatCourse";

const AdminHomeworkTracking = () => {
  // State declarations
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [homeworks, setHomeworks] = useState([]);
  const [statusUpdates, setStatusUpdates] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all courses and their students
  useEffect(() => {
    const coursesRef = ref(db, "courses");
    const unsubscribe = onValue(coursesRef, async (snapshot) => {
      if (snapshot.exists()) {
        const coursesData = snapshot.val();
        const courseIds = Object.keys(coursesData);
        setCourses(courseIds);

        // If a course is selected, fetch its students
        if (selectedCourse && coursesData[selectedCourse].students) {
          const studentsData = coursesData[selectedCourse].students;
          const formattedStudents = Object.entries(studentsData).map(([uid, student]) => ({
            uid,
            ...student,
            courseId: selectedCourse
          }));
          // Sort students alphabetically by name
          const sortedStudents = formattedStudents.sort((a, b) =>
            (a.name || '').localeCompare(b.name || '')
          );
          setStudents(sortedStudents);
        }
      }
    });

    return () => unsubscribe();
  }, [selectedCourse]);

  // Fetch homeworks when course changes
  useEffect(() => {
    if (selectedCourse) {
      const homeworksRef = ref(db, `courses/${selectedCourse}/homeworks`);
      const unsubscribe = onValue(homeworksRef, (snapshot) => {
        if (snapshot.exists()) {
          const homeworksData = snapshot.val();
          const formattedHomeworks = Object.entries(homeworksData).map(([id, hw]) => ({
            id,
            ...hw,
          }));
          setHomeworks(formattedHomeworks);
        } else {
          setHomeworks([]);
        }
      });

      return () => unsubscribe();
    }
  }, [selectedCourse]);

  const filteredStudents = students.filter(student => {
    const matchesSearch = !searchQuery ||
      student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.studentId.toString().includes(searchQuery);
    return matchesSearch;
  }).sort((a, b) => (a.name || '').localeCompare(b.name || '')); // Sort alphabetically by name

  const handleStatusChange = async (studentId, status) => {
    if (!selectedCourse || !selectedHomework) return;

    try {
      const updates = {};
      updates[`courses/${selectedCourse}/students/${studentId}/homeworkStatus/${selectedHomework}`] = status;
      await update(ref(db), updates);
      setStatusUpdates(prev => ({ ...prev, [studentId]: status }));
    } catch (error) {
      console.error("Error updating homework status:", error);
      alert("Failed to update homework status");
    }
  };

  const exportToPDF = () => {
    if (!selectedCourse || !selectedHomework) return;

    const doc = new jsPDF();
    const img = new window.Image();
    img.src = logoPath;
    img.onload = () => {
      const logoW = 42;
      const logoH = logoW * (img.naturalHeight / img.naturalWidth);
      doc.addImage(img, "PNG", 10, 8, logoW, logoH);

      doc.setFontSize(20);
      doc.text("Homework Status Report", 60, 20);
      doc.setFontSize(12);
      doc.text(`Course: ${selectedCourse}`, 60, 30);
      const selectedHwObj = homeworks.find(hw => hw.id === selectedHomework);
      const hwTitle = selectedHwObj ? selectedHwObj.title : selectedHomework;
      doc.text(`Homework: ${hwTitle}`, 60, 36);

      // Sort students alphabetically for PDF
      const sortedStudents = filteredStudents.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );

      const tableData = sortedStudents.map(student => [
        student.studentId,
        student.name,
        student.homeworkStatus?.[selectedHomework] || "Not Submitted"
      ]);

      autoTable(doc, {
        head: [['Student ID', 'Name', 'Status']],
        body: tableData,
        startY: 45,
        theme: 'grid',
        styles: {
          fontSize: 10,
          cellPadding: 5,
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 30 }
        }
      });

      doc.save(`${selectedCourse}_${selectedHomework}_homework_status.pdf`);
    };
  };

  return (
    <div className="p-4 text-gray-900 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-900">Homework Tracking</h1>

      {/* Course selector */}
      <div className="mb-6">
        <label htmlFor="homework-tracking-course" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
        <select
          id="homework-tracking-course"
          value={selectedCourse || ""}
          onChange={(e) => setSelectedCourse(e.target.value || null)}
          className="p-2 border rounded-md text-gray-900 min-w-[200px]"
        >
          <option value="">Select course</option>
          {courses.map(course => (
            <option key={course} value={course}>{formatCourseId(course)}</option>
          ))}
        </select>
      </div>

      {!selectedCourse ? (
        <div className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-b from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-2xl shadow-sm">
          <div className="w-16 h-16 mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">No course selected</h3>
          <p className="text-gray-500 text-sm max-w-sm">Please select a course from the dropdown above to view and manage content.</p>
        </div>
      ) : (
        <>
          {/* Homework Selection */}
          <div className="mb-6 bg-white p-4 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-2 text-gray-900">Select Homework</h2>
            <select
              value={selectedHomework || ""}
              onChange={(e) => setSelectedHomework(e.target.value)}
              className="w-full p-2 border rounded-md text-gray-900"
            >
              <option value="">Select homework</option>
              {homeworks.map(homework => (
                <option key={homework.id} value={homework.id}>{homework.title}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2 border rounded-md text-gray-900"
            />
          </div>

          {/* Export Button */}
          <div className="mb-6">
            <button
              onClick={exportToPDF}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Export to PDF
            </button>
          </div>

          {/* Students Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
                    Student ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map(student => (
                  <tr key={student.uid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {student.studentId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {student.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${student.homeworkStatus?.[selectedHomework] === "done"
                        ? "bg-green-100 text-green-800"
                        : student.homeworkStatus?.[selectedHomework] === "incomplete"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                        }`}>
                        {student.homeworkStatus?.[selectedHomework] || "Not Started"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <select
                        value={student.homeworkStatus?.[selectedHomework] || ""}
                        onChange={(e) => handleStatusChange(student.uid, e.target.value)}
                        className="p-1 border rounded-md text-gray-900"
                      >
                        <option value="">Not Started</option>
                        <option value="incomplete">Incomplete</option>
                        <option value="done">Done</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminHomeworkTracking;